import { useState } from 'react'
import { Send, Zap } from 'lucide-react'
import type { AppNotification, NotificationType } from '@/types'
import {
  Button, Field, Input, Select, SelectContent, SelectItem, SelectTrigger,
  SelectValue, Textarea,
} from '@/components/ui'
import { useDataStore, useNotificationStore, useSettingsStore } from '@/store'

const TYPES: Array<{ value: NotificationType; label: string }> = [
  { value: 'scheduling_request', label: 'Scheduling request (actionable)' },
  { value: 'plan_update', label: 'Plan update' },
  { value: 'song_added', label: 'Song added' },
  { value: 'blockout_conflict', label: 'Blockout conflict' },
  { value: 'general_alert', label: 'General alert' },
]

const BROADCAST = '__broadcast__'

interface Preset {
  label: string
  type: NotificationType
  title: string
  body: string
  priority: AppNotification['priority']
}

const PRESETS: Preset[] = [
  {
    label: 'Serving request',
    type: 'scheduling_request',
    title: 'You have been scheduled',
    body: 'Electric Guitar this Sunday at 9:30. Can you serve?',
    priority: 'high',
  },
  {
    label: 'Plan changed',
    type: 'plan_update',
    title: 'Order of service updated',
    body: 'Two songs were swapped and the sermon was shortened by 5 minutes.',
    priority: 'normal',
  },
  {
    label: 'Urgent alert',
    type: 'general_alert',
    title: 'Rehearsal cancelled',
    body: 'Tonight’s rehearsal is cancelled — we will run through on Sunday morning instead.',
    priority: 'high',
  },
]

/**
 * Admin simulation panel.
 *
 * Pushes notifications into the live feed so the bell badge, the actionable
 * Accept/Decline flow and the offline behaviour can all be exercised without a
 * server — locally or on the deployed BlueHost build.
 */
export function SimulationPanel({ onSent }: { onSent?: () => void }) {
  const people = useDataStore((s) => s.people)
  const plans = useDataStore((s) => s.plans)
  const currentPersonId = useSettingsStore((s) => s.settings?.currentPersonId)
  const simulate = useNotificationStore((s) => s.simulate)

  const [type, setType] = useState<NotificationType>('scheduling_request')
  const [title, setTitle] = useState('You have been scheduled')
  const [body, setBody] = useState('Bass Guitar this Sunday at 9:30. Can you serve?')
  const [priority, setPriority] = useState<AppNotification['priority']>('high')
  const [recipient, setRecipient] = useState<string>(currentPersonId ?? BROADCAST)
  const [planId, setPlanId] = useState<string>(BROADCAST)
  const [sending, setSending] = useState(false)
  const [sentCount, setSentCount] = useState(0)

  function applyPreset(preset: Preset) {
    setType(preset.type)
    setTitle(preset.title)
    setBody(preset.body)
    setPriority(preset.priority)
  }

  async function send() {
    setSending(true)
    try {
      await simulate({
        type,
        title,
        body,
        priority,
        personId: recipient === BROADCAST ? null : recipient,
        link: planId === BROADCAST ? undefined : `/plans/${planId}`,
        relatedPlanId: planId === BROADCAST ? undefined : planId,
      })
      setSentCount((c) => c + 1)
      onSent?.()
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((preset) => (
          <Button key={preset.label} variant="outline" size="sm" onClick={() => applyPreset(preset)}>
            <Zap className="h-3.5 w-3.5" />
            {preset.label}
          </Button>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Type">
          <Select value={type} onValueChange={(value) => setType(value as NotificationType)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Priority">
          <Select
            value={priority}
            onValueChange={(value) => setPriority(value as AppNotification['priority'])}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="high">High</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <Field label="Recipient">
          <Select value={recipient} onValueChange={setRecipient}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={BROADCAST}>Everyone (broadcast)</SelectItem>
              {people.map((person) => (
                <SelectItem key={person.id} value={person.id}>
                  {person.firstName} {person.lastName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Link to plan" hint="Adds a “View details” deep link.">
          <Select value={planId} onValueChange={setPlanId}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={BROADCAST}>No link</SelectItem>
              {plans.slice(0, 12).map((plan) => (
                <SelectItem key={plan.id} value={plan.id}>{plan.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Field label="Title">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>

      <Field label="Message">
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} />
      </Field>

      <div className="flex items-center gap-3">
        <Button onClick={() => void send()} loading={sending}>
          <Send className="h-4 w-4" />
          Trigger notification
        </Button>
        {sentCount > 0 ? (
          <span className="text-sm text-muted-foreground">
            {sentCount} sent this session — check the bell.
          </span>
        ) : null}
      </div>
    </div>
  )
}
